import {
    Address,
    AddressDocument,
    ChangeAddress,
    formatAsFileSize,
    getUpdateAddressEndpoint,
    HousingOccupancyStatus,
    adressSectionFileUploadFrontendSchema,
    isCustomerType,
} from 'cpfr-common';
import { FileUpload, preventSubmit, Spinner } from '@cp-shared-19/frontend-ui';
import {
    Fieldset,
    Form,
    Layout,
    Button,
    ButtonContainer,
    RadioButton,
    FormFieldRadioButtonGroup,
} from '@vwfs-bronson/bronson-react';
import { apiUploadTimeout, reducedMaxFileSize } from 'config';
import { CpDataApi } from 'cp-xhr';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapFileToBase64 } from 'utils/file-utils';
import { EditStatus, EditViewName } from '../../enums';
import { fileTypes, fileUpload, InitialValues, initialValues } from './helpers';
import { EmailInputModal } from 'components/request/form-view/email-input/EmailInputModal';
import { getErrorCode, isDuplicateSubmissionError, isPendingRequestError, useSubmitOnce } from 'utils';

export type FileUploadViewProps = {
    cancelEditing: () => void;
    finishEditing: (newEditStatus: EditStatus, updatedAddressDetails?: Address) => void;
    setCurrentView: (newEditView: EditViewName) => void;
    addressChangeValues: ChangeAddress;
    hasBallonContracts?: boolean;
    customerType: isCustomerType;
    isBillingAddress: boolean;
};

export const FileUploadView: React.FC<FileUploadViewProps> = ({
    cancelEditing,
    addressChangeValues,
    finishEditing,
    hasBallonContracts,
    customerType,
    isBillingAddress,
}) => {
    const { t } = useTranslation(['my-profile', 'loader']);
    const { isSubmitting, run } = useSubmitOnce();
    const [formValues, setFormValues] = useState<InitialValues>(initialValues);
    const requiredFieldValidationTranslation = t('address.file-upload-view.validation.required');
    const wrongSizeValidationTranslation = t('address.file-upload-view.validation.wrong-size');
    const [noEmailError, setNoEmailError] = useState(false);

    const radioItems = [
        {
            label: t('address.file-upload-view.radio-button.hosted-free'),
            value: HousingOccupancyStatus.HOSTED_FREE,
        },
        {
            label: t('address.file-upload-view.radio-button.property'),
            value: HousingOccupancyStatus.PROPERTY_OWNERSHIP,
        },
        {
            label: t('address.file-upload-view.radio-button.rent'),
            value: HousingOccupancyStatus.RENT,
        },
    ];

    const [houseOccuppancy, setHouseOccuppancy] = useState(radioItems[0]);

    const handleSubmit = (values: InitialValues): Promise<void> =>
        run(async () => {
            setFormValues(values);
            setNoEmailError(false);
            const resultFiles: Array<AddressDocument> = await Promise.all(
                Object.entries(values as Omit<InitialValues, 'email'>)
                    .filter((value) => value[1].length && value[0] !== 'email')
                    .map(async (value: [string, File[]]) => ({
                        file: await mapFileToBase64(value[1]),
                        type: fileTypes[value[0] as fileUpload],
                    })),
            );

            const preparedChangeValues: ChangeAddress = {
                houseNumber: addressChangeValues.houseNumber,
                street: addressChangeValues.street,
                streetAdd: addressChangeValues.streetAdd,
                building: addressChangeValues.building,
                door: addressChangeValues.door,
                postalCode: addressChangeValues.postalCode,
                city: addressChangeValues.city,
                country: addressChangeValues.country,
                documents: resultFiles,
                addressType: addressChangeValues.addressType,
                housingOccupancyStatus: houseOccuppancy.value,
                attempts: addressChangeValues.attempts,
                email: values.email,
            };

            try {
                await CpDataApi.put(getUpdateAddressEndpoint(), preparedChangeValues, {
                    timeout: apiUploadTimeout,
                });
            } catch (error) {
                // An earlier address change is still open at the market, so this one
                // was not applied. Saying "success" here would hide that.
                if (isPendingRequestError(error)) {
                    finishEditing(EditStatus.ALREADY_IN_PROGRESS);
                    return;
                }
                // A duplicate is not a failure: the change is already registered,
                // so fall through to the success path instead of offering a retry.
                if (isDuplicateSubmissionError(error)) {
                    finishEditing(EditStatus.SUCCESS);
                    return;
                }
                switch (getErrorCode(error)) {
                    case 'CHECK_FAILURE':
                        finishEditing(EditStatus.CHECK_FAILURE);
                        break;
                    case 'MARKET_API_NOT_REACHABLE':
                        finishEditing(EditStatus.ERROR);
                        break;
                    case 'NOT_ALLOWED_DOCUMENT_TYPE':
                    case 'UNDEFINED_POSTAL_ADDRESS':
                    case 'WRONG_ADDRESS_INFORMATION':
                    case 'CASE_MANAGEMENT_FAILED':
                        finishEditing(EditStatus.ERROR_DOCUMENTS);
                        break;
                    case 'EMAIL_NOT_FOUND_OR_PROVIDED':
                        setNoEmailError(true);
                        //finishEditing(EditStatus.NO_EMAIL_ERROR);
                        break;
                    case 'PROOF_OF_ADDRESS_MANDATORY':
                        finishEditing(EditStatus.PROOF_OF_ADDRESS_MANDATORY);
                        break;
                    default:
                        finishEditing(EditStatus.ERROR);
                        break;
                }
                return;
            }

            const updatedAddress: Address = {
                houseNumber: addressChangeValues.houseNumber,
                street: addressChangeValues.street,
                streetAdd: addressChangeValues.streetAdd,
                building: addressChangeValues.building,
                door: addressChangeValues.door,
                postalCode: addressChangeValues.postalCode,
                city: addressChangeValues.city,
                country: addressChangeValues.country,
            };

            finishEditing(EditStatus.SUCCESS, updatedAddress);
        });

    const errorMessages = {
        proofOfAddress: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        id: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        accomodationCertificate: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        hostId: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        kbis: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        insee: {
            required: requiredFieldValidationTranslation,
            maxSize: wrongSizeValidationTranslation,
        },
        atLeastOne: requiredFieldValidationTranslation,
    };

    return (
        <>
            {isSubmitting && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Formik
                initialValues={formValues}
                validationSchema={adressSectionFileUploadFrontendSchema(
                    customerType.isPrivateCustomer ? 'private' : 'SME',
                    isBillingAddress,
                    errorMessages,
                )}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Form onSubmit={(e) => preventSubmit(e)} data-testid="edit-form">
                        <Fieldset>
                            <Fieldset.Row>
                                <Layout>
                                    <Layout.Item>
                                        <p>
                                            {t(
                                                `address.file-upload-view.${
                                                    customerType.isPrivateCustomer ? 'description' : 'description-sme'
                                                }`,
                                            )}
                                        </p>
                                        {customerType.isPrivateCustomer && (
                                            <Layout.Item default="1/1" s="1/1">
                                                <p>{t('address.file-upload-view.occuppancy')}</p>
                                                <FormFieldRadioButtonGroup>
                                                    {radioItems.map(({ value, label }, index) => (
                                                        <RadioButton
                                                            key={index}
                                                            testId={`goal-${index}`}
                                                            name="goal"
                                                            value={value}
                                                            defaultChecked={index === 0}
                                                            onChange={() => {
                                                                setHouseOccuppancy({ value: value, label: label });
                                                            }}
                                                        >
                                                            {label}
                                                        </RadioButton>
                                                    ))}
                                                </FormFieldRadioButtonGroup>
                                            </Layout.Item>
                                        )}
                                    </Layout.Item>
                                    {customerType.isPrivateCustomer && (
                                        <Layout.Item>
                                            <FileUpload
                                                uploadItemsLabel={''}
                                                name={'proofOfAddress'}
                                                descriptionText={t(
                                                    'address.file-upload-view.proof-of-address.headline',
                                                )}
                                                descriptionSupplementaryText={
                                                    <>
                                                        <li>
                                                            {t('address.file-upload-view.proof-of-address.option1')}
                                                        </li>
                                                        <li>
                                                            {t('address.file-upload-view.proof-of-address.option2')}
                                                        </li>
                                                        <li>
                                                            {t('address.file-upload-view.proof-of-address.option3')}
                                                        </li>
                                                        <li>
                                                            {t('address.file-upload-view.proof-of-address.option4')}
                                                        </li>
                                                    </>
                                                }
                                                buttonText={t('address.file-upload-view.button')}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={reducedMaxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                            />
                                        </Layout.Item>
                                    )}
                                    {!customerType.isPrivateCustomer && (
                                        <Layout.Item>
                                            <FileUpload
                                                uploadItemsLabel={''}
                                                name={'kbis'}
                                                descriptionText={t('address.file-upload-view.kbis.headline')}
                                                buttonText={t('address.file-upload-view.button')}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={reducedMaxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                            />
                                        </Layout.Item>
                                    )}
                                    {!customerType.isPrivateCustomer && (
                                        <Layout.Item>
                                            <FileUpload
                                                uploadItemsLabel={''}
                                                name={'insee'}
                                                descriptionText={t('address.file-upload-view.insee.headline')}
                                                buttonText={t('address.file-upload-view.button')}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={reducedMaxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                            />
                                        </Layout.Item>
                                    )}
                                    {customerType.isPrivateCustomer && hasBallonContracts === true && (
                                        <Layout.Item>
                                            <FileUpload
                                                name={'id'}
                                                uploadItemsLabel={''}
                                                descriptionText={t('address.file-upload-view.id.headline')}
                                                buttonText={t('address.file-upload-view.button')}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={reducedMaxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                            />
                                        </Layout.Item>
                                    )}
                                    {customerType.isPrivateCustomer &&
                                        houseOccuppancy.value === HousingOccupancyStatus.HOSTED_FREE && (
                                            <Layout.Item>
                                                <FileUpload
                                                    name={'accomodationCertificate'}
                                                    uploadItemsLabel={''}
                                                    descriptionText={t(
                                                        'address.file-upload-view.accomodation-certificate.headline',
                                                    )}
                                                    buttonText={t('address.file-upload-view.button')}
                                                    cancelLabel={t('translation:editable-section-nav.cancel')}
                                                    fileSizeFormatter={(bytes: number): string =>
                                                        formatAsFileSize(bytes, 2)
                                                    }
                                                    maxFileSize={reducedMaxFileSize}
                                                    multiple={false}
                                                    validFileFormats={['pdf']}
                                                />
                                            </Layout.Item>
                                        )}
                                    {customerType.isPrivateCustomer &&
                                        houseOccuppancy.value === HousingOccupancyStatus.HOSTED_FREE && (
                                            <Layout.Item>
                                                <FileUpload
                                                    name={'hostId'}
                                                    uploadItemsLabel={''}
                                                    descriptionText={t('address.file-upload-view.host-id.headline')}
                                                    buttonText={t('address.file-upload-view.button')}
                                                    cancelLabel={t('translation:editable-section-nav.cancel')}
                                                    fileSizeFormatter={(bytes: number): string =>
                                                        formatAsFileSize(bytes, 2)
                                                    }
                                                    maxFileSize={reducedMaxFileSize}
                                                    multiple={false}
                                                    validFileFormats={['pdf']}
                                                />
                                            </Layout.Item>
                                        )}
                                </Layout>
                            </Fieldset.Row>
                        </Fieldset>
                        <Fieldset>
                            <Fieldset.Row>
                                <ButtonContainer center>
                                    <Button
                                        secondary
                                        onClick={cancelEditing}
                                        testId="cancel-button"
                                        type="button"
                                        disabled={isSubmitting}
                                    >
                                        {t('contact-details.edit-view.back-button')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            void formik.submitForm();
                                        }}
                                        testId="submit-button"
                                        type="button"
                                        disabled={
                                            isSubmitting || Object.keys(formik.errors).length > 0 || !formik.isValid
                                        }
                                    >
                                        {t('contact-details.edit-view.confirm-button')}
                                    </Button>
                                </ButtonContainer>
                            </Fieldset.Row>
                        </Fieldset>
                        <EmailInputModal
                            show={noEmailError}
                            onClose={() => setNoEmailError(false)}
                            onConfirm={() => {
                                void formik.submitForm();
                            }}
                            disabled={isSubmitting}
                        />
                    </Form>
                )}
            </Formik>
        </>
    );
};
